<template>
  <div class="dashboard-container pa-4">
    <loading-dialog :show="loading" />
    <message-box :show="message.show" :message="message.text" :type="message.type"
      @close-message="message.show = false" />

    <v-row>
      <v-col cols="12">
        <!-- Header Section -->
        <div class="greeting-section mb-6">
          <div class="d-flex justify-space-between align-center position-relative z-index-1">
            <div>
              <div class="greeting-text">My Wallet</div>
              <div class="greeting-subtext">
                Current Balance: <span class="font-weight-bold white--text">{{ summary.formattedBalance || '₦0.00'
                  }}</span>
              </div>
            </div>
            <div>
              <v-btn depressed color="white" class="mr-3" @click="showTopupDialog = true" :loading="topupLoading">
                <v-icon left>mdi-plus-circle</v-icon>
                Topup
              </v-btn>
              <v-btn icon color="white" @click="refreshData" :loading="loading">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Summary Cards -->
        <v-row class="mb-6">
          <v-col cols="12" md="4">
            <v-card class="status-card fill-height" elevation="0">
              <v-card-text class="pa-6">
                <div class="d-flex align-center">
                  <div class="status-icon balance-icon">
                    <v-icon color="#016b4b">mdi-wallet</v-icon>
                  </div>
                  <div class="ml-4">
                    <div class="stat-label">Current Balance</div>
                    <div class="stat-value primary--text">
                      {{ summary.formattedBalance || '₦0.00' }}
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="4">
            <v-card class="status-card fill-height" elevation="0">
              <v-card-text class="pa-6">
                <div class="d-flex align-center">
                  <div class="status-icon credit-icon">
                    <v-icon color="success">mdi-arrow-down-circle</v-icon>
                  </div>
                  <div class="ml-4">
                    <div class="stat-label">Total Credits</div>
                    <div class="stat-value success--text">
                      {{ summary.formattedTotalCredits || '₦0.00' }}
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col cols="12" md="4">
            <v-card class="status-card fill-height" elevation="0">
              <v-card-text class="pa-6">
                <div class="d-flex align-center">
                  <div class="status-icon debit-icon">
                    <v-icon color="error">mdi-arrow-up-circle</v-icon>
                  </div>
                  <div class="ml-4">
                    <div class="stat-label">Total Debits</div>
                    <div class="stat-value error--text">
                      {{ summary.formattedTotalDebits || '₦0.00' }}
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Transaction History -->
        <v-card class="status-card" elevation="0">
          <v-card-text class="pa-6">
            <div class="d-flex align-center mb-6">
              <div class="status-icon history-icon">
                <v-icon color="#0284c7">mdi-history</v-icon>
              </div>
              <div class="ml-4">
                <div class="text-h6 font-weight-medium">Transaction History</div>
                <div class="text-caption grey--text">Recent wallet activity</div>
              </div>
            </div>

            <v-data-table :headers="headers" :items="transactions" :loading="tableLoading"
              :server-items-length="totalTransactions" :options.sync="options"
              :footer-props="{ 'items-per-page-options': [10, 20, 50] }" class="elevation-0">
              <!-- Date Column -->
              <template v-slot:item.creationTime="{ item }">
                <div class="text-body-2 font-weight-medium">
                  {{ formatDate(item.creationTime) }}
                </div>
              </template>

              <!-- Type Column -->
              <template v-slot:item.transactionType="{ item }">
                <v-chip small :color="item.transactionType === 'CREDIT' ? 'success' : 'error'" text-color="white"
                  class="status-chip font-weight-bold">
                  {{ item.transactionType }}
                </v-chip>
              </template>

              <!-- Description Column -->
              <template v-slot:item.description="{ item }">
                <div class="text-body-2 font-weight-medium text-truncate" style="max-width: 300px;">
                  {{ item.description }}
                </div>
                <div class="text-caption grey--text" v-if="item.reference">
                  Ref: {{ item.reference }}
                </div>
              </template>

              <!-- Amount Column -->
              <template v-slot:item.amount="{ item }">
                <span :class="item.transactionType === 'CREDIT' ? 'success--text' : 'error--text'"
                  class="text-body-2 font-weight-bold">
                  {{ item.transactionType === 'CREDIT' ? '+' : '-' }}₦{{ formatNumber(item.amount) }}
                </span>
              </template>

              <!-- Balance After Column -->
              <template v-slot:item.balanceAfter="{ item }">
                <span class="text-body-2 font-weight-medium">
                  ₦{{ formatNumber(item.balanceAfter) }}
                </span>
              </template>

              <template v-slot:no-data>
                <div class="d-flex flex-column align-center justify-center py-8 grey--text text--lighten-1">
                  <v-icon size="48" color="grey lighten-2" class="mb-2">mdi-text-box-search-outline</v-icon>
                  <div>No transactions found</div>
                </div>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Topup Dialog -->
    <v-dialog v-model="showTopupDialog" max-width="400" persistent>
      <v-card>
        <v-card-title class="headline">
          Topup Wallet
        </v-card-title>
        <v-card-text>
          <v-form ref="topupForm" v-model="topupFormValid" lazy-validation>
            <v-text-field v-model="topupAmount" label="Amount (₦)" prefix="₦" type="number" min="100" step="100"
              :rules="amountRules" outlined dense class="mt-4"></v-text-field>
            <v-select v-model="selectedSession" :items="sessions" item-text="name" item-value="id" label="Session"
              outlined dense :loading="loadingSessions" :rules="sessionRules"></v-select>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn dense text @click="closeTopupDialog">Cancel</v-btn>
          <v-btn dense color="primary" @click="initializeTopup" :loading="topupLoading" :disabled="!topupFormValid">
            Proceed to Payment
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Api from '@/main/api'
import ErrorResolver from '@/shared/ErrorResolver'
import LoadingDialog from '@ui/components/LoadingDialog'
import MessageBox from '@ui/components/MessageBox'
import { mapGetters } from 'vuex';

export default {
  name: 'WalletPage',
  components: {
    LoadingDialog,
    MessageBox
  },
  data: () => ({
    loading: false,
    tableLoading: false,
    topupLoading: false,
    loadingSessions: false,
    message: {
      show: false,
      text: '',
      type: 'info'
    },
    showTopupDialog: false,
    topupFormValid: false,
    topupAmount: '',
    selectedSession: null,
    defaultPaymentPlatform: null,
    sessions: [],
    summary: {
      currentBalance: 0,
      totalCredits: 0,
      totalDebits: 0,
      formattedBalance: '₦0.00',
      formattedTotalCredits: '₦0.00',
      formattedTotalDebits: '₦0.00'
    },
    transactions: [],
    totalTransactions: 0,
    options: {
      page: 1,
      itemsPerPage: 10
    },
    headers: [
      { text: 'DATE', value: 'creationTime', width: '180px', sortable: false, class: 'text-uppercase caption font-weight-bold grey--text text--darken-1' },
      { text: 'TYPE', value: 'transactionType', width: '100px', sortable: false, class: 'text-uppercase caption font-weight-bold grey--text text--darken-1' },
      { text: 'DESCRIPTION', value: 'description', sortable: false, class: 'text-uppercase caption font-weight-bold grey--text text--darken-1' },
      { text: 'AMOUNT', value: 'amount', align: 'end', width: '130px', sortable: false, class: 'text-uppercase caption font-weight-bold grey--text text--darken-1' },
      { text: 'BALANCE', value: 'balanceAfter', align: 'end', width: '130px', sortable: false, class: 'text-uppercase caption font-weight-bold grey--text text--darken-1' }
    ],
    amountRules: [
      v => !!v || 'Amount is required',
      v => !isNaN(v) || 'Amount must be a number',
      v => parseFloat(v) >= 100 || 'Minimum amount is ₦100'
    ],
    sessionRules: [
      v => !!v || 'Session is required'
    ]
  }),
  watch: {
    options: {
      handler() {
        this.loadHistory()
      },
      deep: true
    }
  },

  computed: {
    ...mapGetters(['getUser', 'getCurrentSession']),
  },

  methods: {
    showMsg(text, type = 'info') {
      this.message = { show: true, text, type }
    },
    refreshData() {
      this.loadSummary()
      this.loadHistory()
    },
    async loadSummary() {
      this.loading = true
      try {
        const { data } = await Api.wallet.getSummary()
        this.summary = data
      } catch (err) {
        ErrorResolver.checkErrors(this, err)
      } finally {
        this.loading = false
      }
    },
    async loadHistory() {
      this.tableLoading = true
      const { page, itemsPerPage } = this.options
      const size = itemsPerPage === -1 ? 20 : itemsPerPage
      const pageIndex = page > 0 ? page - 1 : 0

      try {
        const { data } = await Api.wallet.getHistory(pageIndex, size)
        this.transactions = data.content
        this.totalTransactions = data.totalElements
      } catch (err) {
        ErrorResolver.checkErrors(this, err)
      } finally {
        this.tableLoading = false
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleString('en-NG', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    formatNumber(val) {
      if (val === null || val === undefined) return '0.00'
      return val.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    async loadSessions() {
      this.loadingSessions = true
      try {
        const result = await Api.session.getSessionsForPayment(this.getUser?.userId)

        console.log('result sessions:', result)

        this.sessions = result.data
      } catch (err) {
        ErrorResolver.checkErrors(this, err)
      } finally {
        this.loadingSessions = false
      }
    },
    async loadWalletSettings() {
      try {
        const response = await Api.wallet.getSettings()
        const settings = response.data

        // Handle platform name extraction
        if (settings.defaultPaymentPlatform && settings.defaultPaymentPlatform.name) {
          this.defaultPaymentPlatform = settings.defaultPaymentPlatform.name
        } else if (typeof settings.defaultPaymentPlatform === 'string') {
          this.defaultPaymentPlatform = settings.defaultPaymentPlatform
        }
      } catch (err) {
        ErrorResolver.checkErrors(this, err)
      }
    },
    closeTopupDialog() {
      this.showTopupDialog = false
      this.topupAmount = ''
      this.selectedSession = null
      if (this.$refs.topupForm) {
        this.$refs.topupForm.reset()
      }
    },
    async initializeTopup() {
      if (!this.$refs.topupForm.validate()) return

      this.topupLoading = true
      try {
        const payload = {
          studentId: this.getUser?.userId,
          amount: parseFloat(this.topupAmount),
          sessionId: this.selectedSession,
          paymentPlatform: this.defaultPaymentPlatform
        }

        const result = await Api.wallet.initializeTopup(payload)

        // Redirect to payment link
        window.location.href = result.data.paymentLink
      } catch (err) {
        ErrorResolver.checkErrors(this, err)
      } finally {
        this.topupLoading = false
      }
    },
    async verifyTopupPayment(reference) {
      try {
        const { data } = await Api.wallet.verifyTopupPayment({ reference })

        if (data.status === 'success') {
          this.showMsg(`Wallet credited successfully with ₦${data.amount.toLocaleString()}. New balance: ₦${data.balance.toLocaleString()}`, 'success')
          this.refreshData()
        } else {
          this.showMsg(data.message || 'Payment verification failed', 'error')
        }
      } catch (err) {
        ErrorResolver.checkErrors(this, err)
      }
    },

    checkPaymentVerification() {
      // Check if returning from payment gateway with reference
      const urlParams = new URLSearchParams(window.location.search)
      const reference = urlParams.get('reference')

      if (reference) {
        this.verifyTopupPayment(reference)
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname)
      }
    }
  },

  created() {
    this.loadSummary()
    this.loadSessions()
    this.loadWalletSettings()
    this.checkPaymentVerification()
  },
}
</script>

<style lang="scss" scoped>
.dashboard-container {
  background-color: #f8fafc;
  min-height: calc(100vh - 64px);
}

// Cards
.status-card {
  background: white;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
  }
}

// Greeting Section
.greeting-section {
  padding: 24px 32px;
  background: linear-gradient(135deg, #016b4b 0%, #018a61 100%);
  border-radius: 16px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(1, 107, 75, 0.2);

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -10%;
    width: 300px;
    height: 300px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 50%;
  }

  .greeting-text {
    font-size: 1.75rem;
    font-weight: 700;
    color: white;
    margin-bottom: 4px;
    letter-spacing: -0.5px;
  }

  .greeting-subtext {
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.9);
  }

  .z-index-1 {
    position: relative;
    z-index: 1;
  }
}

// Stats
.stat-label {
  font-size: 0.75rem;
  color: #64748b;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
}

// Status Icons
.status-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;

  &.balance-icon {
    background: linear-gradient(135deg, #016b4b20 0%, #016b4b40 100%);
  }

  &.credit-icon {
    background: linear-gradient(135deg, #4caf5020 0%, #4caf5040 100%);
  }

  &.debit-icon {
    background: linear-gradient(135deg, #ff525220 0%, #ff525240 100%);
  }

  &.history-icon {
    background: linear-gradient(135deg, #0284c720 0%, #0284c740 100%);
  }
}

.status-chip {
  font-size: 0.75rem !important;
  height: 24px !important;
}

// Responsive Design
@media (max-width: 960px) {
  .greeting-section {
    padding: 20px 24px;

    .greeting-text {
      font-size: 1.5rem;
    }
  }
}
</style>
